import { ReactNode } from 'react'
import styled from 'styled-components'

// transient props -- props we use for conditionally applying styles, but that shouldn't
// be passed by styled-components down to the HTML elements, as they leave ugly console errors
export const transientProps = [
  'active',
  'compact',
  'fullwidth',
  'ghost',
  'grey',
  'inline',
  'leftAlign',
  'noBorder',
  'noDisabledStyle',
  'noHover',
  'omitOpinionatedSvgStyle',
  'rounded',
  'roundedRight',
  'uppercase',
  'white',
  'wider',
  'warning',
] as const

type TransientPropKeys = (typeof transientProps)[number]

type HTMLStringPropKeys = 'css' | 'href' | 'rel' | 'target' | 'type'
type HTMLBooleanPropKeys = 'disabled'
type HTMLPropKeys = HTMLStringPropKeys & HTMLBooleanPropKeys

type TransientProps = Record<TransientPropKeys, boolean>
type HTMLProps = Record<HTMLStringPropKeys, string> &
  Record<HTMLBooleanPropKeys, boolean>

type ButtonProps = Partial<TransientProps & HTMLProps> & {
  as?: string | ReactNode
  onClick?: (...args: any) => any
  style?: object
}

const Button = styled.button
  .attrs(({ uppercase = true }: TransientProps) => ({
    uppercase,
  }))
  .withConfig({
    shouldForwardProp: (prop: TransientPropKeys & HTMLPropKeys) =>
      !transientProps.includes(prop),
  })<ButtonProps>`
  display: ${({ inline }) => (inline ? 'inline-block' : 'block')};
  padding: 1em;

  text-align: center;
  text-transform: ${({ uppercase }) => (uppercase ? 'uppercase' : 'inherit')};
  text-decoration: none !important;

  border: 1px solid transparent;

  font-weight: normal;
  letter-spacing: 1px;

  color: ${({ theme, white }) => (white ? theme.brand.jungle : 'white')};
  &:hover {
    color: ${({ theme, white }) => (white ? theme.brand.jungle : 'white')};
  }

  background-color: ${({ theme, disabled, white, warning }) => {
    if (disabled) {
      return theme.colors.textGreyLight
    }
    if (white) {
      return theme.colors.white
    }
    if (warning) {
      return theme?.['-ds-warning-300']?.value
    }
    return theme.brand.jungle
  }};

  ${({ omitOpinionatedSvgStyle }) =>
    !omitOpinionatedSvgStyle &&
    `
      svg {
        display: inline-block;
        vertical-align: sub;
        width: 20px;
        height: 20px;
        margin-right: 10px;
      }
    `}

  .icon {
    display: inline-block;
    vertical-align: middle;

    margin-top: -4px;
    margin-right: 8px;

    font-size: 24px;
    line-height: 1;
  }

  .icon-bucketlist-heart-on {
    color: #ff748c;
  }

  ${({ noHover }) =>
    !noHover &&
    `
      &:hover {
      text-decoration: none;
      cursor: pointer;
    }
    `};

  ${({ theme, ghost }) =>
    ghost &&
    `
      color: ${theme.brand.jungle};
      &:hover {
        color: ${theme.brand.jungle};
      }
      background-color: transparent;
      border: 1px solid currentColor;
    `};

  ${({ theme, ghost, disabled }) =>
    ghost &&
    disabled &&
    `
      color: ${theme.colors.textGreyLight};
      background-color: transparent;
      border: 1px solid currentColor;
      cursor: not-allowed;
      &:hover {
        color: ${theme.colors.textGreyLight} !important;
        border: 1px solid currentColor;
        cursor: not-allowed;
      }
    `};

  ${({ theme, noDisabledStyle }) =>
    noDisabledStyle &&
    `
      background-color: ${theme.brand.jungle};
    `};

  ${({ grey }) =>
    grey &&
    `
      background-color: #d6d6d6;
    `};

  ${({ wider }) =>
    wider &&
    `
      padding: 1em 2em;
    `};

  ${({ noBorder }) =>
    noBorder &&
    `
      border: 0;
    `};

  ${({ fullwidth }) =>
    fullwidth &&
    `
      width: 100%;
    `};

  ${({ leftAlign }) =>
    leftAlign &&
    `
      text-align: left;
    `};

  ${({ compact }) =>
    compact &&
    `
      padding: .75em 1em;
    `};

  ${({ roundedRight }) =>
    roundedRight &&
    `
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
  `}

  ${({ rounded }) => {
    if (rounded === false) {
      return 'border-radius: 0;'
    }

    return 'border-radius: 6px;'
  }};

  ${({ disabled }) =>
    disabled &&
    `
      pointer-events: none;
    `};
`

export default Button
